(() => {
  if (customElements.get('countdown-timer')) return;

  customElements.define('countdown-timer', class CountdownTimer extends HTMLElement {
    constructor() {
      super();

      this.interval = null;
      this.display = this.querySelector('[data-time-display]');
      this.messageBox = this.querySelector('[data-complete-message-box]');

      this.daysEl = this.querySelector('[data-days-placeholder]');
      this.hoursEl = this.querySelector('[data-hours-placeholder]');
      this.minutesEl = this.querySelector('[data-minutes-placeholder]');
      this.secondsEl = this.querySelector('[data-seconds-placeholder]');

      this.daysBlock = this.querySelector('[data-countdown-unit="days"]');
    }

    connectedCallback() {
      this.update();
      this.interval = window.setInterval(() => this.update(), 1000);
    }

    disconnectedCallback() {
      if (this.interval) {
        window.clearInterval(this.interval);
      }
    }

    get mode() {
      return this.dataset.mode || 'fixed';
    }

    get timezone() {
      const value = (this.dataset.timezone || 'local').trim();
      return value === '' ? 'local' : value;
    }

    get completeMessage() {
      return this.dataset.completeMessage || '';
    }

    get expiredBehavior() {
      return this.dataset.expiredBehavior || 'message';
    }

    get liveWindowMinutes() {
      const value = Number(this.dataset.liveWindowMinutes || 0);
      return Number.isFinite(value) ? value : 0;
    }

    get hideDaysWhenZero() {
      return this.dataset.hideDaysWhenZero === 'true';
    }

    update() {
      const now = new Date();
      const state = this.getTimerState(now);

      if (!state) return;

      if (state.status === 'live') {
        this.showMessage(this.completeMessage);
        return;
      }

      if (state.status === 'expired') {
        this.handleExpired();
        return;
      }

      this.hideMessage();
      this.showDisplay();

      const time = this.getTimeParts(state.diff);

      this.renderTime(time);
    }

    getTimerState(now) {
      if (this.mode === 'daily') {
        return this.getDailyState(now);
      }

      return this.getFixedState(now);
    }

    getDailyState(now) {
      const target = this.getDailyTarget(now);
      const liveWindowMs = this.liveWindowMinutes * 60 * 1000;

      if (liveWindowMs > 0 && now.getTime() >= target && now.getTime() < target + liveWindowMs) {
        return {
          status: 'live',
          diff: 0
        };
      }

      let nextTarget = target;

      if (now.getTime() >= target + liveWindowMs) {
        nextTarget = this.getDailyTarget(now, true);
      }

      return {
        status: 'counting',
        diff: Math.max(0, nextTarget - now.getTime())
      };
    }

    getFixedState(now) {
      const target = this.getFixedTarget();

      if (!target) {
        return null;
      }

      const diff = target - now.getTime();

      if (diff <= 0) {
        return {
          status: 'expired',
          diff: 0
        };
      }

      return {
        status: 'counting',
        diff
      };
    }

    getFixedTarget() {
      const year = Number(this.dataset.year);
      const month = Number(this.dataset.month);
      const day = Number(this.dataset.day);
      const hour = Number(this.dataset.hour);
      const minute = Number(this.dataset.minute);

      if (![year, month, day, hour, minute].every(Number.isFinite)) {
        return null;
      }

      if (this.timezone === 'local') {
        return new Date(year, month - 1, day, hour, minute, 0).getTime();
      }

      return this.zonedTimeToUtc(year, month, day, hour, minute, 0, this.timezone);
    }

    getDailyTarget(now, forceTomorrow = false) {
      const hour = Number(this.dataset.dailyHour || 0);
      const minute = Number(this.dataset.dailyMinute || 0);

      if (this.timezone === 'local') {
        const target = new Date(now);
        target.setHours(hour, minute, 0, 0);

        if (forceTomorrow || target.getTime() <= now.getTime()) {
          target.setDate(target.getDate() + 1);
        }

        return target.getTime();
      }

      const parts = this.getTimeZoneParts(now, this.timezone);
      let target = this.zonedTimeToUtc(parts.year, parts.month, parts.day, hour, minute, 0, this.timezone);

      if (forceTomorrow || target <= now.getTime()) {
        const nextDate = this.addCalendarDays(parts.year, parts.month, parts.day, 1);
        target = this.zonedTimeToUtc(nextDate.year, nextDate.month, nextDate.day, hour, minute, 0, this.timezone);
      }

      return target;
    }

    getTimeParts(diff) {
      const totalSeconds = Math.max(0, Math.floor(diff / 1000));

      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      return {
        days,
        hours,
        minutes,
        seconds
      };
    }

    renderTime(time) {
      if (this.daysEl) this.daysEl.textContent = this.pad(time.days);
      if (this.hoursEl) this.hoursEl.textContent = this.pad(time.hours);
      if (this.minutesEl) this.minutesEl.textContent = this.pad(time.minutes);
      if (this.secondsEl) this.secondsEl.textContent = this.pad(time.seconds);

      if (this.daysBlock && this.hideDaysWhenZero && time.days <= 0) {
        this.daysBlock.hidden = true;
      } else if (this.daysBlock && !this.daysBlock.classList.contains('is-hidden-by-setting')) {
        this.daysBlock.hidden = false;
      }
    }

    handleExpired() {
      if (this.expiredBehavior === 'hide') {
        this.hideDisplay();
        this.hideMessage();
        return;
      }

      if (this.expiredBehavior === 'zero') {
        this.hideMessage();
        this.showDisplay();

        this.renderTime({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0
        });

        return;
      }

      this.showMessage(this.completeMessage);
    }

    showMessage(message) {
      this.hideDisplay();

      if (!this.messageBox) return;

      this.messageBox.textContent = message || '';
      this.messageBox.hidden = !message;
    }

    hideMessage() {
      if (!this.messageBox) return;

      this.messageBox.hidden = true;
      this.messageBox.textContent = '';
    }

    showDisplay() {
      if (!this.display) return;

      this.display.hidden = false;
    }

    hideDisplay() {
      if (!this.display) return;

      this.display.hidden = true;
    }

    pad(value) {
      return String(value).padStart(2, '0');
    }

    addCalendarDays(year, month, day, amount) {
      const date = new Date(Date.UTC(year, month - 1, day + amount, 12, 0, 0));

      return {
        year: date.getUTCFullYear(),
        month: date.getUTCMonth() + 1,
        day: date.getUTCDate()
      };
    }

    getTimeZoneParts(date, timeZone) {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });

      const parts = formatter.formatToParts(date).reduce((acc, part) => {
        if (part.type !== 'literal') {
          acc[part.type] = Number(part.value);
        }

        return acc;
      }, {});

      return {
        year: parts.year,
        month: parts.month,
        day: parts.day,
        hour: parts.hour,
        minute: parts.minute,
        second: parts.second
      };
    }

    zonedTimeToUtc(year, month, day, hour, minute, second, timeZone) {
      let utc = Date.UTC(year, month - 1, day, hour, minute, second);

      for (let i = 0; i < 3; i += 1) {
        const parts = this.getTimeZoneParts(new Date(utc), timeZone);

        const asUtc = Date.UTC(
          parts.year,
          parts.month - 1,
          parts.day,
          parts.hour,
          parts.minute,
          parts.second
        );

        const targetUtc = Date.UTC(year, month - 1, day, hour, minute, second);
        const diff = asUtc - targetUtc;

        utc -= diff;
      }

      return utc;
    }
  });
})();